import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import { Loader2, X, AlertCircle, RefreshCw, Keyboard, Barcode, Bug, Volume2, VolumeX, CheckCircle2 } from 'lucide-react';

// =============================================================================
// BarcodeScanner Component - Barcode-only (QR removed per ITEM 4 requirements)
// =============================================================================
//
// ARCHITECTURE NOTES (DO NOT REGRESS):
// 1. AVOID setState() IN SCAN LOOP - use refs only (scanningRef, lastDetectionRef, etc.)
//    State updates trigger re-renders which cause iOS Safari flicker/black flash.
// 2. VIDEO CONTAINER HAS FIXED HEIGHT (280px) - prevents layout shift on iOS
// 3. VIDEO DIMENSIONS ARE LOCKED after first metadata load - never resize during scanning
// 4. iOS requires user gesture to start camera - handled via needsGesture state
// 5. playsInline + muted + autoplay attributes MUST be set BEFORE srcObject
//
// QA CHECKLIST (run before any scanner changes):
// - iOS Safari: open/close 3x, rotate once, scan 3 barcodes - no flicker
// - iOS Chrome: same as above
// - Android Chrome: verify native BarcodeDetector path works
// - Desktop: verify ZXing fallback works
//
// =============================================================================

// =============================================================================
// Scanner Configuration
// =============================================================================

const FEEDBACK_STORAGE_KEY = 'scanner_feedback_enabled';

// iOS video fit mode - 'contain' is safer but shows letterboxing, 'cover' fills but may crop
// If flicker persists on some iOS devices, try switching to 'contain'
const IOS_VIDEO_OBJECT_FIT = 'cover'; // Options: 'cover' | 'contain'

// Check for native BarcodeDetector support
const hasNativeBarcodeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

// Device detection
const isIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);

// Debug mode - enable via URL param
const isDebugMode = () => {
  if (typeof window === 'undefined') return false;
  const params = new URLSearchParams(window.location.search);
  return params.get('debugScanner') === '1' || params.get('scanDebug') === '1';
};

// =============================================================================
// Barcode-only formats (QR removed per requirements)
// =============================================================================
const BARCODE_FORMATS = ['code_128', 'ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_39', 'itf', 'codabar'];

// =============================================================================
// Scanner Engine - Native BarcodeDetector + ZXing fallback
// =============================================================================

class ScannerEngine {
  constructor() {
    this.nativeDetector = null;
    this.zxingDecoder = null;
    this.zxingBarcodeFormat = null;
    this.zxingHints = null;
    this.activeBackend = 'none';
    this.initialized = false;
    this.nativeFormats = [];
  }

  async initialize() {
    // Try native BarcodeDetector first
    if (hasNativeBarcodeDetector) {
      try {
        const supportedFormats = await window.BarcodeDetector.getSupportedFormats();
        const formatsToUse = BARCODE_FORMATS.filter(f => supportedFormats.includes(f));
        
        if (formatsToUse.length > 0) {
          this.nativeDetector = new window.BarcodeDetector({ formats: formatsToUse });
          this.nativeFormats = formatsToUse;
          this.activeBackend = `Native (${formatsToUse.length} formats)`;
          this.initialized = true;
          console.log('[Scanner] Native BarcodeDetector initialized with:', formatsToUse);
          return { success: true, backend: this.activeBackend };
        }
      } catch (e) {
        console.warn('[Scanner] Native BarcodeDetector failed:', e);
      }
    }
    
    // Fallback to ZXing
    try {
      const { 
        MultiFormatReader, 
        BarcodeFormat, 
        DecodeHintType,
        BinaryBitmap,
        HybridBinarizer,
        HTMLCanvasElementLuminanceSource
      } = await import('@zxing/library');
      
      this.zxingBarcodeFormat = BarcodeFormat;
      this.zxingBinaryBitmap = BinaryBitmap;
      this.zxingHybridBinarizer = HybridBinarizer;
      this.zxingLuminanceSource = HTMLCanvasElementLuminanceSource;
      
      // Map barcode formats only (no QR)
      const zxingFormatMap = {
        'upc_a': BarcodeFormat.UPC_A,
        'upc_e': BarcodeFormat.UPC_E,
        'ean_13': BarcodeFormat.EAN_13,
        'ean_8': BarcodeFormat.EAN_8,
        'code_128': BarcodeFormat.CODE_128,
        'code_39': BarcodeFormat.CODE_39,
        'itf': BarcodeFormat.ITF,
        'codabar': BarcodeFormat.CODABAR
      };
      
      const zxingFormats = BARCODE_FORMATS
        .map(f => zxingFormatMap[f])
        .filter(f => f !== undefined);
      
      this.zxingHints = new Map();
      this.zxingHints.set(DecodeHintType.POSSIBLE_FORMATS, zxingFormats);
      this.zxingHints.set(DecodeHintType.TRY_HARDER, true);
      
      this.zxingDecoder = new MultiFormatReader();
      this.zxingDecoder.setHints(this.zxingHints);
      
      this.activeBackend = `ZXing (${zxingFormats.length} formats)`;
      this.initialized = true;
      console.log('[Scanner] ZXing initialized with formats:', zxingFormats.map(f => BarcodeFormat[f]));
      return { success: true, backend: this.activeBackend };
    } catch (e) {
      console.error('[Scanner] ZXing initialization failed:', e);
      this.activeBackend = 'FAILED';
      return { success: false, backend: 'FAILED', error: e.message };
    }
  }

  async detect(canvas) {
    if (!this.initialized) {
      return { success: false, error: 'Scanner not initialized' };
    }
    
    // Try native detector first
    if (this.nativeDetector) {
      try {
        const barcodes = await this.nativeDetector.detect(canvas);
        if (barcodes.length > 0) {
          return {
            success: true,
            text: barcodes[0].rawValue,
            format: barcodes[0].format,
            backend: 'native'
          };
        }
      } catch (e) {
        if (!this.zxingDecoder) {
          return { success: false, error: e.message };
        }
      }
    }
    
    // Try ZXing fallback
    if (this.zxingDecoder && this.zxingLuminanceSource) {
      try {
        const luminanceSource = new this.zxingLuminanceSource(canvas);
        const binaryBitmap = new this.zxingBinaryBitmap(new this.zxingHybridBinarizer(luminanceSource));
        const result = this.zxingDecoder.decode(binaryBitmap);
        
        if (result) {
          const formatVal = result.getBarcodeFormat();
          const formatName = this.zxingBarcodeFormat ? 
            Object.keys(this.zxingBarcodeFormat).find(k => this.zxingBarcodeFormat[k] === formatVal) || 'unknown'
            : 'unknown';
          
          return {
            success: true,
            text: result.getText(),
            format: formatName,
            backend: 'zxing'
          };
        }
      } catch (e) {
        // NotFoundException is normal - no barcode in frame
        if (e.name !== 'NotFoundException' && e.name !== 'ChecksumException' && e.name !== 'FormatException') {
          return { success: false, error: e.message };
        }
      }
    }
    
    return { success: false };
  }

  reset() {
    if (this.zxingDecoder) {
      try {
        this.zxingDecoder.reset();
      } catch (e) {}
    }
    this.nativeDetector = null;
    this.zxingDecoder = null;
    this.zxingBarcodeFormat = null;
    this.zxingHints = null;
    this.zxingBinaryBitmap = null;
    this.zxingHybridBinarizer = null;
    this.zxingLuminanceSource = null;
    this.activeBackend = 'none';
    this.initialized = false;
    this.nativeFormats = [];
  }
}

// =============================================================================
// Main BarcodeScanner Component - Barcode only, no QR
// =============================================================================

export const BarcodeScanner = ({ open, onClose, onScan }) => {
  // Refs - stable across renders (CRITICAL: avoid setState in scan loop)
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const scannerEngineRef = useRef(null);
  const animationFrameRef = useRef(null);
  const mountedRef = useRef(true);
  const scanningRef = useRef(false);
  const lastDetectionRef = useRef({ value: null, count: 0, timestamp: 0 });
  const manualEntryTimerRef = useRef(null);
  const audioRef = useRef(null);
  const hasPlayedBeepRef = useRef(false);
  const audioUnlockedRef = useRef(false);
  const initStartedRef = useRef(false);
  const videoDimensionsLockedRef = useRef(false); // Lock dimensions after first metadata
  const lockedVideoDimensionsRef = useRef({ width: 0, height: 0 });
  
  // Minimal state - only what's needed for UI updates
  const [devices, setDevices] = useState([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState('');
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [manualCode, setManualCode] = useState('');
  const [detectedValue, setDetectedValue] = useState(null);
  const [scanSuccess, setScanSuccess] = useState(false);
  const [needsGesture, setNeedsGesture] = useState(false);
  const [backend, setBackend] = useState('initializing...');
  
  // Feedback setting
  const [feedbackEnabled, setFeedbackEnabled] = useState(() => {
    try {
      const stored = localStorage.getItem(FEEDBACK_STORAGE_KEY);
      return stored === null ? true : stored === 'true';
    } catch {
      return true;
    }
  });

  const debugMode = isDebugMode();

  // ==========================================================================
  // Audio handling
  // ==========================================================================
  
  const initializeAudio = useCallback(async () => {
    if (!feedbackEnabled || audioUnlockedRef.current) return;
    
    const sources = ['/sounds/scanner-beep.wav', '/sounds/scanner-beep.mp3'];
    
    for (const src of sources) {
      try {
        const audio = new Audio(src);
        audio.preload = 'auto';
        audio.volume = 0;
        await audio.play();
        audio.pause();
        audio.currentTime = 0;
        audio.volume = 0.7;
        
        audioRef.current = audio;
        audioUnlockedRef.current = true;
        console.log('[Scanner] Audio unlocked:', src);
        return;
      } catch (err) {
        console.warn('[Scanner] Audio unlock failed for', src);
      }
    }
  }, [feedbackEnabled]);

  const playBeep = useCallback(async () => {
    if (!feedbackEnabled || hasPlayedBeepRef.current) return;
    hasPlayedBeepRef.current = true;
    
    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0;
        await audioRef.current.play();
      } catch (err) {
        console.warn('[Scanner] Beep failed:', err);
      }
    }
  }, [feedbackEnabled]);

  const handleFeedbackToggle = useCallback((enabled) => {
    setFeedbackEnabled(enabled);
    try {
      localStorage.setItem(FEEDBACK_STORAGE_KEY, String(enabled));
    } catch {}
    
    if (!enabled && audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
      audioUnlockedRef.current = false;
    } else if (enabled) {
      initializeAudio();
    }
  }, [initializeAudio]);

  // ==========================================================================
  // Cleanup
  // ==========================================================================
  
  const cleanup = useCallback(() => {
    console.log('[Scanner] Cleanup');
    scanningRef.current = false;
    hasPlayedBeepRef.current = false;
    setScanSuccess(false);
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    
    if (scannerEngineRef.current) {
      scannerEngineRef.current.reset();
      scannerEngineRef.current = null;
    }
    
    if (manualEntryTimerRef.current) {
      clearTimeout(manualEntryTimerRef.current);
      manualEntryTimerRef.current = null;
    }
    
    lastDetectionRef.current = { value: null, count: 0, timestamp: 0 };
    
    // Reset locked dimensions for next camera start
    videoDimensionsLockedRef.current = false;
    lockedVideoDimensionsRef.current = { width: 0, height: 0 };
  }, []);

  // ==========================================================================
  // Start camera - called once on mount or user gesture
  // ==========================================================================
  
  const startCamera = useCallback(async (deviceId = null) => {
    if (!mountedRef.current) return;
    
    cleanup();
    setError(null);
    setCameraReady(false);
    setScanning(false);
    setShowManualEntry(false);
    setDetectedValue(null);
    setNeedsGesture(false);
    
    console.log('[Scanner] Starting camera...');
    
    try {
      // Initialize scanner engine first
      scannerEngineRef.current = new ScannerEngine();
      const engineResult = await scannerEngineRef.current.initialize();
      setBackend(engineResult.backend);
      
      if (!engineResult.success) {
        throw new Error('Scanner engine initialization failed');
      }
      
      // Camera constraints - optimized for barcode scanning
      const constraints = {
        video: deviceId 
          ? { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, min: 15 } }
          : { facingMode: { ideal: 'environment' }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30, min: 15 } },
        audio: false
      };
      
      console.log('[Scanner] getUserMedia constraints:', JSON.stringify(constraints));
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (!mountedRef.current) {
        stream.getTracks().forEach(t => t.stop());
        return;
      }
      
      streamRef.current = stream;
      
      // Get track info and try continuous autofocus
      const videoTrack = stream.getVideoTracks()[0];
      const settings = videoTrack.getSettings();
      const capabilities = videoTrack.getCapabilities?.() || {};
      
      if (capabilities.focusMode?.includes('continuous')) {
        try {
          await videoTrack.applyConstraints({ advanced: [{ focusMode: 'continuous' }] });
          console.log('[Scanner] Continuous autofocus enabled');
        } catch (e) {
          console.log('[Scanner] Could not set autofocus');
        }
      }
      
      // Update device selection
      if (settings.deviceId && !deviceId) {
        setSelectedDeviceId(settings.deviceId);
      }
      
      // Enumerate devices
      const deviceList = await navigator.mediaDevices.enumerateDevices();
      setDevices(deviceList.filter(d => d.kind === 'videoinput'));
      
      // Attach to video element - CRITICAL iOS attributes
      if (videoRef.current) {
        const video = videoRef.current;
        
        // Set attributes BEFORE srcObject (critical for iOS)
        video.playsInline = true;
        video.muted = true;
        video.autoplay = true;
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('muted', 'true');
        video.setAttribute('autoplay', 'true');
        
        video.srcObject = stream;
        
        // Wait for metadata
        await new Promise((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Video timeout')), 10000);
          
          const onLoaded = () => {
            clearTimeout(timeout);
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            resolve();
          };
          
          const onError = (e) => {
            clearTimeout(timeout);
            video.removeEventListener('loadedmetadata', onLoaded);
            video.removeEventListener('error', onError);
            reject(new Error('Video error'));
          };
          
          video.addEventListener('loadedmetadata', onLoaded);
          video.addEventListener('error', onError);
        });
        
        // Play video
        try {
          await video.play();
          console.log('[Scanner] Video playing:', video.videoWidth, 'x', video.videoHeight);
          
          // LOCK DIMENSIONS after first successful play (prevents iOS resize flicker)
          if (!videoDimensionsLockedRef.current && video.videoWidth > 0 && video.videoHeight > 0) {
            lockedVideoDimensionsRef.current = { 
              width: video.videoWidth, 
              height: video.videoHeight 
            };
            videoDimensionsLockedRef.current = true;
            console.log('[Scanner] Dimensions locked:', lockedVideoDimensionsRef.current);
          }
        } catch (playError) {
          if (playError.name === 'NotAllowedError') {
            throw new Error('Autoplay blocked. Tap to start.');
          }
          throw playError;
        }
        
        if (video.videoWidth === 0 || video.videoHeight === 0) {
          throw new Error('Camera stream has zero dimensions');
        }
        
        setCameraReady(true);
      }
    } catch (err) {
      console.error('[Scanner] Camera error:', err);
      
      if (!mountedRef.current) return;
      
      if (err.name === 'NotAllowedError') {
        setError('Camera access denied. Please allow camera access.');
      } else if (err.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else if (err.name === 'NotReadableError') {
        setError('Camera is in use by another app.');
      } else {
        setError(`Camera error: ${err.message}`);
      }
    }
  }, [cleanup]);

  // ==========================================================================
  // Scan loop - CRITICAL: No setState calls during scanning to prevent iOS flicker
  // ==========================================================================
  
  const startScanning = useCallback(() => {
    if (!videoRef.current || !canvasRef.current || scanningRef.current) return;
    
    console.log('[Scanner] Starting scan loop');
    setScanning(true);
    scanningRef.current = true;
    
    // Show manual entry after 10 seconds
    manualEntryTimerRef.current = setTimeout(() => {
      if (mountedRef.current && !showManualEntry) {
        setShowManualEntry(true);
      }
    }, 10000);
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    
    const scan = async () => {
      if (!mountedRef.current || !scanningRef.current) return;
      
      // Wait for enough video data
      if (video.readyState < video.HAVE_ENOUGH_DATA) {
        animationFrameRef.current = requestAnimationFrame(scan);
        return;
      }
      
      // Use locked dimensions if available (prevents iOS resize during scan)
      const vw = videoDimensionsLockedRef.current 
        ? lockedVideoDimensionsRef.current.width 
        : video.videoWidth;
      const vh = videoDimensionsLockedRef.current 
        ? lockedVideoDimensionsRef.current.height 
        : video.videoHeight;
      
      if (vw === 0 || vh === 0) {
        animationFrameRef.current = requestAnimationFrame(scan);
        return;
      }
      
      // Set canvas dimensions ONCE (avoid resize during scan loop)
      if (canvas.width !== vw || canvas.height !== vh) {
        canvas.width = vw;
        canvas.height = vh;
        console.log('[Scanner] Canvas set to:', vw, 'x', vh);
      }
      
      // Draw frame and detect
      ctx.drawImage(video, 0, 0, vw, vh);
      
      if (scannerEngineRef.current?.initialized) {
        try {
          const result = await scannerEngineRef.current.detect(canvas);
          
          if (result.success && result.text) {
            const detectedText = result.text.trim();
            const now = Date.now();
            
            if (debugMode) {
              console.log('[Scanner] Detected:', result.format, detectedText);
            }
            
            // Debounce: require 2 consistent reads OR 500ms
            const lastDet = lastDetectionRef.current;
            const timeSinceFirst = now - lastDet.timestamp;
            
            if (lastDet.value === detectedText) {
              lastDet.count++;
              
              if (lastDet.count >= 2 || timeSinceFirst >= 500) {
                console.log('[Scanner] Confirmed:', detectedText);
                
                // Stop scanning
                scanningRef.current = false;
                setScanning(false);
                setDetectedValue(detectedText);
                setScanSuccess(true);
                
                // Haptic feedback
                try {
                  if (navigator.vibrate) navigator.vibrate(50);
                } catch {}
                
                // Audio beep
                playBeep();
                
                // Toast
                const display = detectedText.length > 30 ? detectedText.substring(0, 27) + '...' : detectedText;
                toast.success(`Scanned: ${display}`, { duration: 2000, icon: <CheckCircle2 className="w-4 h-4" /> });
                
                // Brief pause then close
                setTimeout(() => {
                  cleanup();
                  onScan(detectedText);
                  onClose();
                }, 250);
                return;
              }
            } else {
              // New value
              lastDetectionRef.current = { value: detectedText, count: 1, timestamp: now };
              setDetectedValue(detectedText);
            }
          }
        } catch (err) {
          // Ignore detection errors
        }
      }
      
      animationFrameRef.current = requestAnimationFrame(scan);
    };
    
    animationFrameRef.current = requestAnimationFrame(scan);
  }, [cleanup, onScan, onClose, showManualEntry, debugMode, playBeep]);

  // ==========================================================================
  // Effects
  // ==========================================================================
  
  // Handle open/close
  useEffect(() => {
    mountedRef.current = true;
    initStartedRef.current = false;
    
    if (!open) {
      cleanup();
      setCameraReady(false);
      setScanning(false);
      setError(null);
      setShowManualEntry(false);
      setManualCode('');
      setDetectedValue(null);
      setNeedsGesture(false);
    }
    
    return () => {
      mountedRef.current = false;
      cleanup();
    };
  }, [open, cleanup]);

  // Auto-start camera when dialog opens (once)
  useEffect(() => {
    if (open && !cameraReady && !error && !initStartedRef.current) {
      initStartedRef.current = true;
      
      // iOS Safari needs user gesture for camera
      if (isIOS()) {
        setNeedsGesture(true);
      } else {
        startCamera();
      }
    }
  }, [open, cameraReady, error, startCamera]);

  // Start scanning when camera is ready
  useEffect(() => {
    if (cameraReady && !scanningRef.current && !error) {
      startScanning();
    }
  }, [cameraReady, error, startScanning]);

  // ==========================================================================
  // Handlers
  // ==========================================================================
  
  const handleDeviceChange = useCallback((deviceId) => {
    setSelectedDeviceId(deviceId);
    initStartedRef.current = false;
    startCamera(deviceId);
  }, [startCamera]);

  const handleClose = useCallback(() => {
    cleanup();
    onClose();
  }, [cleanup, onClose]);

  const handleManualSubmit = useCallback((e) => {
    e.preventDefault();
    const code = manualCode.trim();
    if (code) {
      const display = code.length > 30 ? code.substring(0, 27) + '...' : code;
      toast.success(`Scanned: ${display}`, { duration: 2000, icon: <CheckCircle2 className="w-4 h-4" /> });
      cleanup();
      onScan(code);
      onClose();
    }
  }, [manualCode, cleanup, onScan, onClose]);

  const handleRetry = useCallback(() => {
    setError(null);
    initStartedRef.current = false;
    startCamera(selectedDeviceId || null);
  }, [startCamera, selectedDeviceId]);

  const handleStartCamera = useCallback(() => {
    // Unlock audio on user gesture (iOS)
    initializeAudio();
    setNeedsGesture(false);
    startCamera();
  }, [startCamera, initializeAudio]);

  // ==========================================================================
  // Render
  // ==========================================================================
  
  if (!open) return null;

  const showDeviceSelector = devices.length > 1;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && handleClose()}>
      <DialogContent className="max-w-lg sm:max-w-xl p-0 overflow-hidden">
        <DialogHeader className="p-4 pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle className="flex items-center gap-2">
              <Barcode className="w-5 h-5" /> Scan Barcode
            </DialogTitle>
            
            {/* Feedback Toggle */}
            <div className="flex items-center gap-2" data-testid="feedback-toggle-container">
              <label 
                htmlFor="feedback-toggle" 
                className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none"
              >
                {feedbackEnabled ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">Sound</span>
              </label>
              <Switch
                id="feedback-toggle"
                checked={feedbackEnabled}
                onCheckedChange={handleFeedbackToggle}
                data-testid="feedback-toggle"
              />
            </div>
          </div>
        </DialogHeader>
        
        <div className="px-4 pb-4">
          {/* Device selector */}
          {showDeviceSelector && cameraReady && (
            <div className="mb-3">
              <Select value={selectedDeviceId} onValueChange={handleDeviceChange}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select camera" />
                </SelectTrigger>
                <SelectContent>
                  {devices.map((device, idx) => (
                    <SelectItem key={device.deviceId} value={device.deviceId}>
                      {device.label || `Camera ${idx + 1}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Error state */}
          {error ? (
            <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-6 text-center">
              <AlertCircle className="w-12 h-12 mx-auto mb-3 text-destructive" />
              <p className="text-destructive font-medium mb-4">{error}</p>
              <Button variant="outline" onClick={handleRetry}>
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            </div>
          ) : needsGesture ? (
            /* iOS: Require user gesture */
            <div className="bg-muted rounded-lg p-8 text-center">
              <Barcode className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
              <p className="text-muted-foreground mb-4">Tap the button below to start the camera</p>
              <Button onClick={handleStartCamera} size="lg" data-testid="start-camera-btn">
                Start Camera
              </Button>
            </div>
          ) : (
            /* Camera view - FIXED HEIGHT to prevent iOS layout shift */
            <div 
              className="relative bg-black rounded-lg overflow-hidden"
              style={{ height: '280px', minHeight: '280px', maxHeight: '280px' }}
            >
              <video
                ref={videoRef}
                playsInline
                muted
                autoPlay
                data-testid="scanner-video"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  // iOS video fit mode - 'contain' safer for some devices, 'cover' fills frame
                  objectFit: isIOS() ? IOS_VIDEO_OBJECT_FIT : 'cover',
                  backgroundColor: 'black'
                }}
              />
              
              {/* Hidden canvas for frame capture */}
              <canvas ref={canvasRef} style={{ display: 'none' }} />
              
              {/* Debug overlay */}
              {debugMode && (
                <div className="absolute top-2 left-2 bg-black/80 text-white text-xs px-2 py-1 rounded font-mono z-50">
                  <Bug className="w-3 h-3 inline mr-1" />
                  {backend}
                </div>
              )}
              
              {/* Scanning overlay */}
              {scanning && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="border-2 border-primary rounded-lg relative w-[85%] h-[55%]">
                    <div className="absolute -top-1 -left-1 w-8 h-8 border-t-4 border-l-4 border-primary rounded-tl" />
                    <div className="absolute -top-1 -right-1 w-8 h-8 border-t-4 border-r-4 border-primary rounded-tr" />
                    <div className="absolute -bottom-1 -left-1 w-8 h-8 border-b-4 border-l-4 border-primary rounded-bl" />
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 border-b-4 border-r-4 border-primary rounded-br" />
                    <div className="absolute left-2 right-2 h-0.5 bg-primary/80 animate-scan-line" />
                    <div className="absolute left-4 right-4 top-1/2 -translate-y-1/2 h-px bg-primary/40" />
                  </div>
                </div>
              )}
              
              {/* Success overlay */}
              {scanSuccess && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-in fade-in duration-150">
                  <div className="border-4 border-green-500 bg-green-500/20 rounded-lg relative w-[85%] h-[55%] flex items-center justify-center">
                    <div className="bg-green-500 rounded-full p-3">
                      <CheckCircle2 className="w-10 h-10 text-white" />
                    </div>
                  </div>
                </div>
              )}
              
              {/* Detected value indicator */}
              {detectedValue && scanning && (
                <div className="absolute bottom-2 left-2 right-2 bg-green-500/90 text-white text-center py-2 px-3 rounded text-sm font-medium">
                  Detected: {detectedValue}
                </div>
              )}
              
              {/* Loading overlay */}
              {!cameraReady && !error && !needsGesture && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                  <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-white mx-auto mb-3" />
                    <p className="text-white text-sm">Accessing camera...</p>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Help text */}
          {cameraReady && !error && (
            <p className="text-sm text-muted-foreground text-center mt-3">
              Hold steady and align barcode within the frame
            </p>
          )}
          
          {/* Manual entry */}
          {(showManualEntry || error) && (
            <form onSubmit={handleManualSubmit} className="mt-4 p-3 bg-muted rounded-lg">
              <p className="text-sm text-muted-foreground mb-2 flex items-center gap-2">
                <Keyboard className="w-4 h-4" />
                {error ? "Can't access camera? Enter the code manually:" : "Having trouble? Enter the code manually:"}
              </p>
              <div className="flex gap-2">
                <Input
                  value={manualCode}
                  onChange={(e) => setManualCode(e.target.value)}
                  placeholder="Enter barcode..."
                  className="flex-1"
                  autoFocus={error}
                  data-testid="manual-code-input"
                />
                <Button type="submit" disabled={!manualCode.trim()} data-testid="manual-submit-btn">
                  Submit
                </Button>
              </div>
            </form>
          )}
          
          {/* Supported formats */}
          <p className="text-xs text-muted-foreground/60 text-center mt-2">
            Supports: UPC-A, UPC-E, EAN-13, EAN-8, Code 128, Code 39
          </p>
          
          {/* Action buttons */}
          <div className="flex justify-between items-center mt-4">
            {!showManualEntry && cameraReady && (
              <Button variant="ghost" size="sm" onClick={() => setShowManualEntry(true)} data-testid="show-manual-btn">
                <Keyboard className="w-4 h-4 mr-1" /> Manual Entry
              </Button>
            )}
            <div className="flex-1" />
            <Button variant="outline" onClick={handleClose} data-testid="cancel-btn">
              <X className="w-4 h-4 mr-2" /> Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BarcodeScanner;
